import {
  useTheme,
  Box,
  Typography,
  alpha,
  IconButton,
  Tooltip,
} from "@mui/material";
import NotificationsOutlinedIcon from "@mui/icons-material/NotificationsOutlined";
import ArrowLeftIcon from "@mui/icons-material/ArrowLeft";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import { useState, useEffect, useMemo, useCallback } from "react";

const COLORS = ["#2a5fa9", "#4cbed8", "#65b986", "#8fc68b", "#ffffff"];

const AlertCard = ({ nom_alerte, count, color, index, total, onClick }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  return (
    <Tooltip title={`${nom_alerte}: ${count} alertes`} arrow placement="top">
      <Box
        onClick={onClick}  
        sx={{
          bgcolor: alpha(color, isDark ? 0.25 : 0.1),
          border: `1px solid ${alpha(color, 0.4)}`,
          borderRadius: 3,
          p: 2.5,
          textAlign: "center",
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          minWidth: 140,
          height: 120,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          cursor: "pointer",
          position: "relative",
          overflow: "hidden",
          "&::before": {
            content: '""',
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            height: "4px",
            bgcolor: color,
            transform: "scaleX(0)",
            transformOrigin: "left",
            transition: "transform 0.3s ease",
          },
          "&:hover": {
            transform: "translateY(-8px)",
            boxShadow: `0 12px 28px ${alpha(color, 0.35)}`,
            bgcolor: alpha(color, isDark ? 0.35 : 0.2),
            "&::before": { transform: "scaleX(1)" },
          },
          animation: "fadeInUp 0.5s ease-out forwards",
          animationDelay: `${index * 0.1}s`,
          opacity: 0,
          "@keyframes fadeInUp": {
            from: { opacity: 0, transform: "translateY(20px)" },
            to: { opacity: 1, transform: "translateY(0)" },
          },
        }}
      >
        <Box sx={{ position: "relative", display: "inline-flex", justifyContent: "center", mb: 1 }}>
          <NotificationsOutlinedIcon
            sx={{
              fontSize: 32,
              color,
              transition: "transform 0.3s ease",
              ".MuiBox-root:hover &": { transform: "rotate(15deg) scale(1.1)" },
            }}
          />
          {count > 99 && (
            <Box
              sx={{
                position: "absolute",
                top: -4, right: -4,
                bgcolor: "error.main",
                color: "white",
                borderRadius: "50%",
                width: 16, height: 16,
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 10, fontWeight: "bold",
              }}
            >
              !
            </Box>
          )}
        </Box>

        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", px: 1 }}
        >
          {nom_alerte}
        </Typography>

        <Typography
          variant="h5"
          fontWeight={800}
          color={color}
          sx={{ transition: "transform 0.3s ease", ".MuiBox-root:hover &": { transform: "scale(1.15)" } }}
        >
          {count.toLocaleString()}
        </Typography>

        <Box
          sx={{
            position: "absolute",
            bottom: 0, left: 0,
            height: "3px",
            width: `${(count / total) * 100}%`,
            bgcolor: alpha(color, 0.5),
            transition: "width 0.6s ease-out",
          }}
        />
      </Box>
    </Tooltip>
  );
};

const BarChart = ({ data = [], onBarClick }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const chartData = useMemo(() => data[0]?.data || [], [data]);

  const [currentPage, setCurrentPage] = useState(0);
  const itemsPerPage = 3;
  const totalPages = Math.ceil(chartData.length / itemsPerPage);

  useEffect(() => { setCurrentPage(0); }, [chartData]);

  const currentItems = chartData.slice(
    currentPage * itemsPerPage,
    currentPage * itemsPerPage + itemsPerPage
  );

  const goNext = useCallback(() => setCurrentPage((p) => (p + 1) % totalPages), [totalPages]);
  const goPrev = useCallback(() => setCurrentPage((p) => (p - 1 + totalPages) % totalPages), [totalPages]);

  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === "ArrowLeft") goPrev();
      if (e.key === "ArrowRight") goNext();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [goNext, goPrev]);

  const stats = useMemo(() => {
    if (!chartData.length) return { total: 0, max: 0, avg: 0 };
    const counts = chartData.map((d) => d.y);
    return {
      total: counts.reduce((a, b) => a + b, 0),
      max: Math.max(...counts),
      avg: counts.reduce((a, b) => a + b, 0) / counts.length,
    };
  }, [chartData]);

  if (!chartData.length) {
    return (
      <Box height="100%" display="flex" flexDirection="column" alignItems="center" justifyContent="center" p={3}>
        <NotificationsOutlinedIcon sx={{ fontSize: 60, opacity: 0.3, mb: 2, color: COLORS[0] }} />
        <Typography variant="h6" color="text.secondary">Aucune alerte disponible</Typography>
        <Typography variant="body2" color="text.secondary" mt={1} sx={{ opacity: 0.7 }}>
          Les alertes s'afficheront ici
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      p={3}
      borderRadius="16px"
      bgcolor={isDark ? "#121212" : "#f8fafc"}
      sx={{
        position: "relative",
        boxShadow: isDark
          ? `0 4px 20px ${alpha("#000", 0.3)}`
          : `0 4px 20px ${alpha("#000", 0.05)}`,
      }}
    >
      {/* En-tête */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
        pb={2}
        borderBottom={`1px solid ${alpha("#2a5fa9", 0.2)}`}
      >
        <Box>
          <Typography variant="h6" fontWeight={700} color="#2a5fa9">
            Alertes Actives
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {chartData.length} types • {stats.total.toLocaleString()} total
          </Typography>
        </Box>
        <Box display="flex" gap={3}>
          <Box textAlign="right">
            <Typography variant="caption" color="text.secondary">Maximum</Typography>
            <Typography variant="body2" fontWeight={700} color="#2a5fa9">
              {stats.max.toLocaleString()}
            </Typography>
          </Box>
          <Box textAlign="right">
            <Typography variant="caption" color="text.secondary">Moyenne</Typography>
            <Typography variant="body2" fontWeight={700} color="#2a5fa9">
              {Math.round(stats.avg).toLocaleString()}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Cartes + pagination */}
      <Box position="relative" display="flex" alignItems="center">
        {totalPages > 1 && (
          <Tooltip title="Précédent" placement="right">
            <IconButton
              onClick={goPrev}
              sx={{
                position: "absolute", left: -20, zIndex: 10,
                color: "#2a5fa9", bgcolor: alpha("#2a5fa9", 0.1),
                "&:hover": { bgcolor: alpha("#2a5fa9", 0.2), transform: "scale(1.15)" },
                transition: "all 0.3s ease",
              }}
            >
              <ArrowLeftIcon />
            </IconButton>
          </Tooltip>
        )}

        <Box
          flex={1}
          display="flex"
          flexWrap="wrap"
          gap={{ xs: 1, sm: 1.5, md: 2 }}
          justifyContent="center"
          alignItems="stretch"
          sx={{ width: "100%", overflowX: { xs: "hidden", sm: "visible" } }}
        >
          {currentItems.map((item, i) => (
            <Box
              key={`${currentPage}-${i}`}
              sx={{
                flex: {
                  xs: "0 1 calc(50% - 8px)",
                  sm: "0 1 calc(33.33% - 12px)",
                  md: "0 1 calc(33.33% - 16px)",
                },
                display: "flex",
                justifyContent: "center",
                minWidth: { xs: "140px", sm: "160px" },
              }}
            >
              <AlertCard
                nom_alerte={item.x}
                count={item.y}
                color={COLORS[i % COLORS.length]}
                index={i}
                total={stats.max}
                onClick={() => onBarClick?.(item.x)}  
              />
            </Box>
          ))}
        </Box>

        {totalPages > 1 && (
          <Tooltip title="Suivant" placement="left">
            <IconButton
              onClick={goNext}
              sx={{
                position: "absolute", right: -20, zIndex: 10,
                color: "#2a5fa9", bgcolor: alpha("#2a5fa9", 0.1),
                "&:hover": { bgcolor: alpha("#2a5fa9", 0.2), transform: "scale(1.15)" },
                transition: "all 0.3s ease",
              }}
            >
              <ArrowRightIcon />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {/* Indicateurs de page */}
      {totalPages > 1 && (
        <Box display="flex" justifyContent="center" gap={0.75} mt={2.5}>
          {Array.from({ length: totalPages }).map((_, i) => (
            <Box
              key={i}
              onClick={() => setCurrentPage(i)}
              sx={{
                width: i === currentPage ? 20 : 8,
                height: 8,
                borderRadius: 4,
                bgcolor: i === currentPage ? "#2a5fa9" : alpha("#2a5fa9", 0.3),
                cursor: "pointer",
                transition: "all 0.3s ease",
                "&:hover": { bgcolor: alpha("#2a5fa9", 0.6) },
              }}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

export default BarChart;